import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/db";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, ShieldAlert, Trash2, User } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = async () => {
    try {
      const data = await db.adminListUsers();
      setUsers(data || []);
    } catch (err) {
      toast.error("Erro ao carregar usuários: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") loadUsers();
  }, [user]);

  if (user?.role !== "admin") {
    return <Navigate to="/app/dashboard" replace />;
  }

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    if (u.id === user.id) {
      return toast.error("Você não pode remover seu próprio acesso de admin aqui.");
    }
    if (!window.confirm(`Tem certeza que deseja promover/rebaixar ${u.name}?`)) return;
    
    try {
      await db.adminUpdateUser(u.id, { role: newRole });
      toast.success("Permissão atualizada com sucesso");
      loadUsers();
    } catch (err) {
      toast.error("Erro ao atualizar: " + (err.message || err));
    }
  };

  const toggleAccess = async (u) => {
    const newStatus = u.access_status === "active" ? "inactive" : "active";
    if (u.id === user.id) {
      return toast.error("Você não pode bloquear a si mesmo.");
    }
    
    try {
      await db.adminUpdateUser(u.id, { access_status: newStatus });
      toast.success(`Acesso ${newStatus === "active" ? "liberado" : "bloqueado"} com sucesso`);
      loadUsers();
    } catch (err) {
      toast.error("Erro ao atualizar: " + (err.message || err));
    }
  };

  const deleteUser = async (u) => {
    if (u.id === user.id) return toast.error("Você não pode excluir a si mesmo.");
    if (!window.confirm(`PERIGO: Deseja apagar permanentemente o usuário ${u.name} e TODOS os dados dele?`)) return;

    try {
      await db.adminDeleteUser(u.id);
      toast.success("Usuário excluído com sucesso");
      loadUsers();
    } catch (err) {
      toast.error("Erro ao excluir: " + (err.message || err));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
  };

  return (
    <div data-testid="admin-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-[#D9381E]" />
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E]">Super Admin</div>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl">Gestão de Usuários</h1>
        <p className="text-[#4A4A4A] mt-2">Visualize e gerencie as contas de todos os usuários do RevisaPro.</p>
      </div>

      <Card className="p-6 bg-white border border-black/10 rounded-none overflow-x-auto">
        {loading ? (
          <div className="text-center py-10 font-mono text-sm">Carregando usuários...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 font-mono text-sm">Nenhum usuário encontrado.</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-black/10 font-mono text-xs uppercase text-[#8A8A8A]">
                <th className="pb-3 pr-4 font-normal">Usuário</th>
                <th className="pb-3 pr-4 font-normal">E-mail</th>
                <th className="pb-3 pr-4 font-normal">Nível / Acesso</th>
                <th className="pb-3 pr-4 font-normal">Métricas</th>
                <th className="pb-3 pr-4 font-normal">Cadastro</th>
                <th className="pb-3 font-normal text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-black/5 hover:bg-[#F6F5F0]/50 transition-colors">
                  <td className="py-4 pr-4 font-serif text-lg flex items-center gap-2">
                    {u.role === "admin" ? <ShieldAlert className="w-4 h-4 text-[#D9381E]" /> : <User className="w-4 h-4 text-black/50" />}
                    {u.name}
                    {u.id === user.id && <Badge className="ml-2 text-[10px] py-0 px-1.5 h-4 bg-black/10 text-black hover:bg-black/10">Você</Badge>}
                  </td>
                  <td className="py-4 pr-4 font-sans text-black/70">{u.email}</td>
                  <td className="py-4 pr-4 flex flex-col gap-2 items-start">
                    <Badge className={u.role === "admin" ? "bg-[#D9381E] text-white rounded-none" : "bg-black/10 text-black rounded-none"}>
                      {u.role === "admin" ? "Admin" : "User"}
                    </Badge>
                    <Badge className={u.access_status === "active" ? "bg-green-100 text-green-800 rounded-none border-green-200" : "bg-red-100 text-red-800 rounded-none border-red-200"}>
                      {u.access_status === "active" ? "Ativo" : "Bloqueado"}
                    </Badge>
                  </td>
                  <td className="py-4 pr-4 text-xs font-mono text-black/60">
                    <div>Propostas: {u.stats?.proposals || 0}</div>
                    <div>Projetos: {u.stats?.projects || 0}</div>
                  </td>
                  <td className="py-4 pr-4 font-mono text-black/60 text-xs">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="py-4 text-right space-x-2 whitespace-nowrap">
                    <Button 
                      variant={u.access_status === "active" ? "outline" : "default"} 
                      size="sm" 
                      className={`rounded-none h-8 text-xs ${u.access_status !== "active" ? "bg-[#D9381E] text-white hover:bg-[#B82914]" : ""}`}
                      onClick={() => toggleAccess(u)}
                      disabled={u.id === user.id}
                    >
                      {u.access_status === "active" ? "Bloquear" : "Liberar Acesso"}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-none h-8 text-xs"
                      onClick={() => toggleRole(u)}
                      disabled={u.id === user.id}
                    >
                      {u.role === "admin" ? "Remover Admin" : "Fazer Admin"}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="rounded-none h-8 w-8 text-[#D9381E] hover:bg-[#D9381E] hover:text-white"
                      onClick={() => deleteUser(u)}
                      disabled={u.id === user.id}
                      title="Apagar Usuário"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
